import React, { useEffect, useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useAuth } from '@/context/AuthContext';
import { CookieConsent as CookieConsentType, storeCookieConsent, getUserCookieConsent, updateCookieConsent } from '@/lib/actions';
import { useToast } from '@/components/ui/use-toast';

export const CookieConsent = () => {
  const [cookieConsent, setCookieConsent] = useState<CookieConsentType | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const [isFetching, setIsFetching] = useState(false); // Added isFetching state

  const [showBanner, setShowBanner] = useState(() => {
    if (typeof window === 'undefined') return true; // Default to show banner SSR or if localStorage not available
    const localConsentItem = localStorage.getItem('cookieConsent');
    if (localConsentItem) {
      try {
        const parsedConsent = JSON.parse(localConsentItem) as CookieConsentType;
        return !parsedConsent.accepted;
      } catch (error) {
        console.error('Failed to parse cookie consent from localStorage:', error);
        localStorage.removeItem('cookieConsent');
        return true; // Show banner if parsing failed
      }
    }
    return true; // Default to show banner if no local consent
  });

  useEffect(() => {
    let isMounted = true;

    const fetchAndSetConsent = async () => {
      if (typeof window === 'undefined') return;

      if (user?.id) { // Authenticated user
        if (isFetching) return; // Prevent multiple fetches
        setIsFetching(true);
        try {
          const dbConsent = await getUserCookieConsent(user.id);
          if (!isMounted) return;

          if (dbConsent) {
            setCookieConsent(dbConsent);
            setShowBanner(!dbConsent.accepted);
            localStorage.setItem('cookieConsent', JSON.stringify(dbConsent));
          } else {
            // No consent in DB. If localStorage has consent, it remains.
            // If no local storage item existed, banner remains true (from initialState).
            const localConsentItem = localStorage.getItem('cookieConsent');
            if (!localConsentItem && isMounted) { // If truly no consent anywhere
                 setShowBanner(true);
            }
          }
        } catch (error) {
          console.error('Error fetching user cookie consent from DB:', error);
          // Keep localStorage version if DB fetch fails, or show banner if no local
           if (isMounted) {
            const localConsentItem = localStorage.getItem('cookieConsent');
            if (!localConsentItem) {
                setShowBanner(true);
            }
            // Optionally, you could toast a muted error here if desired
           }
        } finally {
          if (isMounted) {
            setIsFetching(false);
          }
        }
      } else { // Anonymous user - rely on localStorage
        const localConsentItem = localStorage.getItem('cookieConsent');
        if (localConsentItem) {
          try {
            const parsedConsent = JSON.parse(localConsentItem) as CookieConsentType;
            if (isMounted) {
              setCookieConsent(parsedConsent);
              setShowBanner(!parsedConsent.accepted);
            }
          } catch (error) {
            console.error('Failed to parse cookie consent from localStorage for anonymous user:', error);
            if (isMounted) setShowBanner(true);
            localStorage.removeItem('cookieConsent');
          }
        } else {
          if (isMounted) setShowBanner(true); // No local consent, show banner
        }
      }
    };

    fetchAndSetConsent();

    return () => {
      isMounted = false;
    };
  }, [user?.id, isFetching]); // Changed dependency to user?.id and added isFetching (though isFetching might be redundant here if managed inside)
  // Let's refine the dependencies: [user?.id] should be enough if isFetching logic is self-contained in the effect.
  // Re-evaluating: isFetching in dependency array can cause loop if not careful.
  // The effect should run when user.id changes. The isFetching guard inside prevents re-entry.
  // So dependency should be primarily user?.id.

  // Corrected useEffect dependency
  // The previous dependency array [user?.id, isFetching] would cause an infinite loop
  // because setIsFetching inside the effect would trigger it again.
  // The correct way is to use isFetching as a guard inside the effect,
  // and depend only on external values that should trigger a re-fetch, like user?.id.

  // Rewriting the useEffect with corrected dependencies and isFetching logic:

  useEffect(() => {
    let isMounted = true;

    const fetchConsentForUser = async (userId: string) => {
      if (isFetching || typeof window === 'undefined') return;
      
      setIsFetching(true);
      try {
        const dbConsent = await getUserCookieConsent(userId);
        if (!isMounted) return;

        if (dbConsent) {
          setCookieConsent(dbConsent);
          setShowBanner(!dbConsent.accepted);
          localStorage.setItem('cookieConsent', JSON.stringify(dbConsent));
        } else {
          const localConsentItem = localStorage.getItem('cookieConsent');
          if (!localConsentItem && isMounted) {
            setShowBanner(true);
          }
        }
      } catch (error) {
        console.error('Error fetching user cookie consent from DB:', error);
        if (isMounted) {
          const localConsentItem = localStorage.getItem('cookieConsent');
          if (!localConsentItem) {
            setShowBanner(true);
          }
        }
      } finally {
        if (isMounted) {
          setIsFetching(false);
        }
      }
    };

    const loadAnonymousConsent = () => {
        if (typeof window === 'undefined') return;
        const localConsentItem = localStorage.getItem('cookieConsent');
        if (localConsentItem) {
          try {
            const parsedConsent = JSON.parse(localConsentItem) as CookieConsentType;
            if (isMounted) {
              setCookieConsent(parsedConsent);
              setShowBanner(!parsedConsent.accepted);
            }
          } catch (error) {
            console.error('Failed to parse cookie consent from localStorage (anonymous):', error);
            if (isMounted) setShowBanner(true);
            localStorage.removeItem('cookieConsent');
          }
        } else {
          if (isMounted) setShowBanner(true);
        }
    };

    if (user?.id) {
      fetchConsentForUser(user.id);
    } else {
      loadAnonymousConsent();
    }

    return () => {
      isMounted = false;
    };
  }, [user?.id]); // Correct dependency: only user?.id


  const handleConsent = async (accepted: boolean) => {
    if (typeof window === 'undefined') return; // Guard against server-side execution if any

    try {
      // Get IP address - consider moving this to a utility or abstracting if used elsewhere
      let ipAddress = null;
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        if (response.ok) {
          const data = await response.json();
          ipAddress = data.ip;
        } else {
          console.warn('Could not fetch IP address:', response.status);
        }
      } catch (error) {
        console.error('Error fetching IP address:', error);
      }

      let success = false;
      const consentDataToStore: Omit<CookieConsentType, 'id' | 'created_at' | 'updated_at'> & { updated_at?: string, created_at?: string, id?: string } = {
        accepted: accepted,
        ...(user?.id && { user_id: user.id }),
        ...(ipAddress && { ip_address: ipAddress }),
      };

      if (user?.id) {
        const existingUserConsent = cookieConsent?.user_id === user.id ? cookieConsent : await getUserCookieConsent(user.id);
        if (existingUserConsent?.id) {
          success = await updateCookieConsent(existingUserConsent.id, accepted);
          consentDataToStore.id = existingUserConsent.id;
          consentDataToStore.created_at = existingUserConsent.created_at;
        } else {
          success = await storeCookieConsent(consentDataToStore as CookieConsentType); // Type assertion might be needed if storeCookieConsent is strict
        }
      } else if (ipAddress) {
         // For anonymous users, check if there's an existing consent by IP to update
         // This is a bit complex as getAnonymousCookieConsent isn't designed for this update flow directly
         // Simplification: always store new or let storeCookieConsent handle upsert logic if it has it
        success = await storeCookieConsent(consentDataToStore as CookieConsentType);
      } else if (!user?.id && !ipAddress) {
          // Fallback if IP cannot be fetched for anonymous user, rely on localStorage only
          console.warn("Cannot store cookie consent for anonymous user without IP address.");
      }


      const finalConsentObject: CookieConsentType = {
        id: consentDataToStore.id || cookieConsent?.id || undefined, // try to preserve ID
        user_id: user?.id,
        ip_address: ipAddress || cookieConsent?.ip_address,
        accepted: accepted,
        created_at: consentDataToStore.created_at || cookieConsent?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      localStorage.setItem('cookieConsent', JSON.stringify(finalConsentObject));
      setCookieConsent(finalConsentObject);
      setShowBanner(false);

      if (accepted) {
        toast({
          title: "Cookies u pranuan",
          description: "Faleminderit për pranimin e cookies në faqen tonë.",
        });
      } else {
        toast({
          title: "Cookies u refuzuan",
          description: "Ju keni refuzuar cookies. Disa veçori mund të jenë të kufizuara.",
        });
      }
    } catch (error) {
      console.error('Error handling cookie consent:', error);
      toast({
        title: "Gabim",
        description: "Nuk u ruajtën dot preferencat tuaja të cookies.",
        variant: "destructive",
      });
    }
  };
  
  const handleAccept = () => handleConsent(true);
  const handleDecline = () => handleConsent(false);
  
  if (!showBanner) {
    return null;
  }
  
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-50">
      <div className="container mx-auto flex flex-col md:flex-row items-center justify-between">
        <div className="mb-4 md:mb-0 pr-10">
          <h3 className="font-bold text-lg mb-2">Ne vlerësojmë privatësinë tuaj</h3>
          <p className="text-gray-600 text-sm">
            Ne përdorim cookies për të përmirësuar eksperiencën tuaj të shfletimit, për të shërbyer reklama ose përmbajtje të personalizuara dhe për të analizuar trafikun tonë. Duke klikuar "Pranoni", ju pajtoheni me përdorimin e cookies nga ne.
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleDecline}>
            Refuzoj
          </Button>
          <Button onClick={handleAccept}>
            Pranoj
          </Button>
          <button 
            onClick={() => setShowBanner(false)} 
            className="absolute top-3 right-3 md:relative md:top-auto md:right-auto ml-2"
            aria-label="Mbyll bannerin e cookies"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
